import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { usePermissions } from '../hooks/usePermissions';
import { landsOnCatalogView } from '../utils/roles';

/**
 * DefaultRedirect component that redirects users to their appropriate landing
 * page based on their role:
 * - catalog-owner, admin, legacy-manager, delegate → /my-objects
 * - viewer → /home
 *
 * The owner/manager/delegate set is centralised in ``landsOnCatalogView`` so
 * this stays in lockstep with NavigationBar's Catalog-tab gating (previously
 * the role lists were duplicated and could drift — e.g. legacy-manager was an
 * "owner" in NavigationBar but fell through to /home here).
 *
 * Delegates land on /my-objects (the unified Catalog view) like any other
 * catalog user: ActiveCatalogContext auto-selects their first delegated
 * catalog and the Catalog dropdown lets them switch between the catalogs they
 * have access to. (The standalone delegate dashboard was removed in favour of
 * this unified flow.)
 */
export const DefaultRedirect = () => {
  const { permissions, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Catalog owners, admins, legacy managers, and delegates land on the
  // unified Catalog (My Objects) view; everyone else (viewers) goes to Home.
  if (landsOnCatalogView(permissions?.role)) {
    return <Navigate to="/my-objects" replace />;
  }

  return <Navigate to="/home" replace />;
};
